import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useOrganizationSettingsQuery } from '@/hooks/queries/useOrganizationSettingsQuery'
import { createLogger } from '@/lib/logger'
import { useOrganizationStore } from '@/store/organization.store'
import { useLocation } from 'react-router-dom'

import {
  formatEntityTerm,
  ModuleTypeEnum,
  SupplierTerminologyMode,
} from '@scrambl/shared'

const logger = createLogger('contexts/SupplierTerminologyContext')

type SupplierTerm = {
  singular: string
  plural: string
  Singular: string
  Plural: string
  mode: SupplierTerminologyMode
  isVendor: boolean
  isLoading: boolean
  organizationId?: string
}

const TERMS: Record<
  SupplierTerminologyMode,
  Pick<SupplierTerm, 'singular' | 'plural'>
> = {
  [SupplierTerminologyMode.SUPPLIER]: {
    singular: 'supplier',
    plural: 'suppliers',
  },
  [SupplierTerminologyMode.VENDOR]: {
    singular: 'vendor',
    plural: 'vendors',
  },
}

const CACHE_KEY_PREFIX = 'scrambl:supplier-terminology'
const DEFAULT_MODE = SupplierTerminologyMode.VENDOR
const modeCache = new Map<string, SupplierTerminologyMode>()
const listeners = new Set<
  (organizationId: string, mode: SupplierTerminologyMode) => void
>()
let currentStaticOrgId: string | null = null

const SupplierTerminologyContext = createContext<SupplierTerm | null>(null)

function isSupplierTerminologyMode(
  value: unknown,
): value is SupplierTerminologyMode {
  return (
    value === SupplierTerminologyMode.SUPPLIER ||
    value === SupplierTerminologyMode.VENDOR
  )
}

function getCacheKey(organizationId: string) {
  return `${CACHE_KEY_PREFIX}:${organizationId}`
}

function readCachedMode(organizationId?: string | null) {
  if (!organizationId) return undefined

  const inMemory = modeCache.get(organizationId)
  if (inMemory) return inMemory

  if (typeof window === 'undefined') return undefined

  try {
    const stored = window.localStorage.getItem(getCacheKey(organizationId))
    if (isSupplierTerminologyMode(stored)) {
      modeCache.set(organizationId, stored)
      return stored
    }
  } catch {
    return undefined
  }

  return undefined
}

function writeCachedMode(
  organizationId: string,
  mode: SupplierTerminologyMode,
) {
  modeCache.set(organizationId, mode)
  currentStaticOrgId = organizationId

  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(getCacheKey(organizationId), mode)
  } catch {
    // Local storage can be unavailable in private or embedded contexts.
  }
}

function readModeFromSettings(settings: unknown) {
  const mode = (settings as any)?.modules?.[ModuleTypeEnum.SUPPLIER]
    ?.terminology
  return isSupplierTerminologyMode(mode) ? mode : undefined
}

function buildSupplierTerm(
  mode: SupplierTerminologyMode,
  organizationId?: string,
  isLoading = false,
): SupplierTerm {
  const terms = TERMS[mode]

  return {
    ...terms,
    Singular: formatEntityTerm(terms.singular, true, false),
    Plural: formatEntityTerm(terms.plural, true, false),
    mode,
    isVendor: mode === SupplierTerminologyMode.VENDOR,
    isLoading,
    organizationId,
  }
}

export function SupplierTerminologyProvider({
  children,
}: {
  children: ReactNode
}) {
  const { currentOrganization } = useOrganizationStore()
  const location = useLocation()
  const organizationId = currentOrganization?.id
  const isOnOrganizationsPage = location.pathname === '/organizations'
  const queryEnabled = !!organizationId && !isOnOrganizationsPage

  const { data: settingsData, isLoading } = useOrganizationSettingsQuery({
    enabled: queryEnabled,
  })
  const [version, setVersion] = useState(0)

  const settingsMode = readModeFromSettings(settingsData?.effectiveSettings)
  const organizationMode = readModeFromSettings(currentOrganization?.settings)
  const cachedMode = readCachedMode(organizationId)
  const mode = settingsMode ?? organizationMode ?? cachedMode ?? DEFAULT_MODE

  useEffect(() => {
    if (!organizationId) {
      currentStaticOrgId = null
      return
    }

    currentStaticOrgId = organizationId
  }, [organizationId])

  useEffect(() => {
    if (!organizationId || !settingsMode) return
    writeCachedMode(organizationId, settingsMode)
  }, [organizationId, settingsMode])

  useEffect(() => {
    const listener = (updatedOrganizationId: string) => {
      if (updatedOrganizationId === organizationId) {
        setVersion(current => current + 1)
      }
    }

    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [organizationId])

  const value = useMemo(
    () => buildSupplierTerm(mode, organizationId, isLoading),
    [mode, organizationId, isLoading, version],
  )

  return (
    <SupplierTerminologyContext.Provider value={value}>
      {children}
    </SupplierTerminologyContext.Provider>
  )
}

export function updateSupplierTerminology(
  organizationId: string,
  mode: SupplierTerminologyMode,
) {
  writeCachedMode(organizationId, mode)
  listeners.forEach(listener => listener(organizationId, mode))
}

export function setCurrentStaticSupplierTerminologyOrganization(
  organizationId: string | null,
) {
  currentStaticOrgId = organizationId
}

export function getSupplierTerminologyStatic(organizationId?: string) {
  const orgId = organizationId || currentStaticOrgId
  const mode = readCachedMode(orgId) ?? DEFAULT_MODE
  return buildSupplierTerm(mode, orgId ?? undefined)
}

export function useSupplierTerminology() {
  const context = useContext(SupplierTerminologyContext)

  if (!context) {
    if (process.env.NODE_ENV === 'development') {
      logger.warn(
        'useSupplierTerminology called outside SupplierTerminologyProvider - returning defaults',
      )
    }

    return buildSupplierTerm(DEFAULT_MODE)
  }

  return context
}
